// Default colors (fallback values)
const defaultColors = {
  primary: '#f95f1f',
  secondary: '#6c757d',
  success: '#4caf50',
  danger: '#f44336',
  warning: '#ff9800',
  info: '#2196f3',
  light: '#f8f9fa',
  dark: '#212121',
  muted: '#6c757d',
  white: '#ffffff',
  Light_Grey: "#A2A2A5",

  // Add shades of primary
  primaryLight: '#fa7d47',
  primaryDark: '#e54d0e',
  // Add any other colors you need
  background: '#f8f9fa',
  backgroundLight: '#F3F3E0', // Light cream/beige background color
  border: '#dee2e6',
  text: {
    primary: '#212121',
    secondary: '#6c757d',
    muted: '#8a8a8a'
  }
};

// Function to get user theme colors from localStorage
const getUserThemeColors = () => {
  try {
    const authData = localStorage.getItem('auth_user');
    if (!authData) return { primary: null, accent: null, text: null, theme: null };

    const parsed = JSON.parse(authData);
    const user = parsed?.user;
    
    if (!user) return { primary: null, accent: null, text: null, theme: null };

    // Check company theme: if DEFAULT, use default colors; if CUSTOM, use agence colors
    const companyTheme = user?.company?.theme;
    
    if (companyTheme === 'DEFAULT') {
      // Use default colors (return null to use defaults)
      return { primary: null, accent: null, text: null, theme: 'DEFAULT' };
    }
    
    if (companyTheme === 'CUSTOM') {
      // Get theme colors from user.company.agence
      const agence = user?.company?.agence;
      return {
        primary: agence?.themePrimaryColor || null,
        accent: agence?.themeAccentColor || null,
        text: agence?.themeTextColor || null,
        theme: 'CUSTOM'
      };
    }

    // Fallback: if theme is not set or unknown, use defaults
    return { primary: null, accent: null, text: null, theme: null };
  } catch (error) {
    console.error('Error reading theme colors from localStorage:', error);
    return { primary: null, accent: null, text: null, theme: null };
  }
};

// Function to get colors object with dynamic values
export const getColors = () => {
  const themeColors = getUserThemeColors();
  const primaryColor = themeColors.primary || defaultColors.primary;
  
  // Calculate backgroundLight: use primary with 0.1 opacity for CUSTOM theme, otherwise use default
  const backgroundLight = themeColors.theme === 'CUSTOM' 
    ? hexToRgba(primaryColor, 0.1)
    : defaultColors.backgroundLight;
  
  return {
    ...defaultColors,
    // Override with theme colors if available, otherwise use defaults
    primary: primaryColor,
    backgroundLight: backgroundLight,
    // Recalculate primary shades if primary color changed
    primaryLight: themeColors.primary 
      ? adjustColorBrightness(themeColors.primary, 20) 
      : defaultColors.primaryLight,
    primaryDark: themeColors.primary 
      ? adjustColorBrightness(themeColors.primary, -20) 
      : defaultColors.primaryDark,
  };
};

// Helper function to convert hex to RGB
const hexToRgb = (hex) => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return { r, g, b };
};

// Helper function to convert hex to rgba string
const hexToRgba = (hex, opacity = 1) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Helper function to adjust color brightness
const adjustColorBrightness = (hex, percent) => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Adjust brightness
  const newR = Math.min(255, Math.max(0, r + (r * percent / 100)));
  const newG = Math.min(255, Math.max(0, g + (g * percent / 100)));
  const newB = Math.min(255, Math.max(0, b + (b * percent / 100)));
  
  // Convert back to hex
  const toHex = (n) => {
    const hex = Math.round(n).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
};

// Export colors object that reads dynamically (for backward compatibility)
// This creates a getter object that reads from localStorage on each access
export const colors = new Proxy(defaultColors, {
  get(target, prop) {
    const themeColors = getUserThemeColors();
    const primaryColor = themeColors.primary || target.primary;
    
    // Handle special cases
    if (prop === 'primary') {
      return primaryColor;
    }
    if (prop === 'backgroundLight') {
      // Use primary with 0.1 opacity for CUSTOM theme, otherwise use default #F3F3E0
      return themeColors.theme === 'CUSTOM'
        ? hexToRgba(primaryColor, 0.1)
        : target.backgroundLight;
    }
    if (prop === 'primaryLight') {
      return themeColors.primary 
        ? adjustColorBrightness(primaryColor, 20) 
        : target.primaryLight;
    }
    if (prop === 'primaryDark') {
      return themeColors.primary 
        ? adjustColorBrightness(primaryColor, -20) 
        : target.primaryDark;
    }
    
    // Return default for other properties
    return target[prop];
  }
});

// Function to get color with opacity
export const getColorWithOpacity = (color, opacity) => {
  const colorsObj = getColors();
  const colorValue = colorsObj[color] || color;
  
  // If it's already an rgba string, extract the RGB values
  if (colorValue.startsWith('rgba')) {
    const rgbaMatch = colorValue.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (rgbaMatch) {
      return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${opacity})`;
    }
  }
  
  // If it's a hex color, convert it
  if (colorValue.startsWith('#')) {
    return hexToRgba(colorValue, opacity);
  }
  
  // Fallback: try to parse as hex
  const hex = colorValue.replace('#', '');
  if (hex.length === 6) {
    return hexToRgba(`#${hex}`, opacity);
  }
  
  return colorValue;
};

// React hook for reactive color updates (use this in React components)
export const useColors = () => {
  // This will be imported dynamically to avoid circular dependencies
  const { useSelector } = require('react-redux');
  
  const user = useSelector((state) => state.auth?.user);
  
  // Check company theme: if DEFAULT, use default colors; if CUSTOM, use agence colors
  const companyTheme = user?.company?.theme;
  let themePrimaryColor = null;
  let themeAccentColor = null;
  let themeTextColor = null;
  
  if (companyTheme === 'CUSTOM') {
    const agence = user?.company?.agence;
    themePrimaryColor = agence?.themePrimaryColor || null;
    themeAccentColor = agence?.themeAccentColor || null;
    themeTextColor = agence?.themeTextColor || null;
  }
  // If DEFAULT or not set, theme colors remain null and defaults will be used
  
  const primaryColor = themePrimaryColor || defaultColors.primary;
  
  // Use primary with 0.1 opacity for CUSTOM theme, otherwise use default #F3F3E0
  const backgroundLight = companyTheme === 'CUSTOM'
    ? hexToRgba(primaryColor, 0.1)
    : defaultColors.backgroundLight;
  
  return {
    ...defaultColors,
    primary: primaryColor,
    backgroundLight: backgroundLight,
    primaryLight: themePrimaryColor 
      ? adjustColorBrightness(themePrimaryColor, 20) 
      : defaultColors.primaryLight,
    primaryDark: themePrimaryColor 
      ? adjustColorBrightness(themePrimaryColor, -20) 
      : defaultColors.primaryDark,
    // Add theme-specific colors if available
    ...(themeAccentColor && { accent: themeAccentColor }),
    ...(themeTextColor && { textColor: themeTextColor }),
  };
};

export default colors; 